import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  X,
  Users,
  MapPin,
  Activity,
  Building2,
  Clock,
  ArrowRight,
  TrendingUp,
  User,
  Home,
  Shield,
  ExternalLink,
  History
} from 'lucide-react';
import { cn } from '../utils/cn';
import { condoApi } from '../services/api';
import { useCustomer } from '../contexts/CustomerContext';
import { usePersonDetailContext } from '../contexts/PersonDetailContext';
import { logger } from '../services/logger';
import { getAbsoluteImageUrl } from '../utils/imageUrl';

interface SearchResult {
  id: string;
  type: 'person' | 'zone' | 'log' | 'customer' | 'unit' | 'device';
  title: string;
  subtitle?: string;
  description?: string;
  lastAccess?: string;
  status?: 'online' | 'offline' | 'active' | 'inactive';
  metadata?: Record<string, any>;
  action?: () => void;
  href?: string;
  // Photo fields for person results
  identity_photo_url?: string;
  profile_picture_url?: string;
}

interface GlobalSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({ isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { selectedCustomer } = useCustomer();
  const { openPersonDetail } = usePersonDetailContext();

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('recentSearches');
    if (saved) {
      setRecentSearches(JSON.parse(saved));
    }
  }, []);

  // Auto-focus search input when opened
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Save search to history
  const saveSearchToHistory = useCallback((term: string) => {
    if (!term.trim()) return;
    const updated = [term, ...recentSearches.filter(s => s !== term)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem('recentSearches', JSON.stringify(updated));
  }, [recentSearches]);

  // Real API search function
  const performSearch = useCallback(async (term: string) => {
    if (!term.trim()) {
      setResults([]);
      return;
    }

    setIsSearching(true);

    try {
      logger.debug('GlobalSearch', 'Performing search', { term, customerId: selectedCustomer?.id });

      const response = await condoApi.globalSearch({
        q: term,
        customerId: selectedCustomer?.id,
        limit: 20
      });

      const searchResults: SearchResult[] = response.data.results || [];

      logger.info('GlobalSearch', 'Search completed', {
        term,
        resultsCount: searchResults.length
      });

      setResults(searchResults);
      setSelectedIndex(0);
    } catch (error: any) {
      logger.error('GlobalSearch', 'Search failed', error, { term });

      // Fallback to empty results on error
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [selectedCustomer]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, performSearch]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => (prev + 1) % Math.max(results.length, 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => (prev - 1 + results.length) % Math.max(results.length, 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            handleResultClick(results[selectedIndex]);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onClose]);

  const handleResultClick = (result: SearchResult) => {
    saveSearchToHistory(searchTerm);

    // Handle different result types
    if (result.type === 'person') {
      // Open person detail modal instead of navigating
      openPersonDetail(result.id);
      onClose();
      setSearchTerm('');
    } else if (result.action) {
      result.action();
      onClose();
      setSearchTerm('');
    } else if (result.href) {
      navigate(result.href);
      onClose();
      setSearchTerm('');
    }
  };

  const handleRecentSearchClick = (term: string) => {
    setSearchTerm(term);
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('recentSearches');
  };

  const getResultIcon = (type: SearchResult['type']) => {
    switch (type) {
      case 'person':
        return User;
      case 'zone':
        return MapPin;
      case 'log':
        return Activity;
      case 'customer':
        return Building2;
      case 'unit':
        return Home;
      case 'device':
        return Shield;
      default:
        return Search;
    }
  };

  const getResultTypeLabel = (type: SearchResult['type']) => {
    switch (type) {
      case 'person':
        return 'Pessoa';
      case 'zone':
        return 'Zona';
      case 'log':
        return 'Log';
      case 'customer':
        return 'Cliente';
      case 'unit':
        return 'Unidade';
      case 'device':
        return 'Dispositivo';
      default:
        return '';
    }
  };

  const getStatusColor = (status?: SearchResult['status']) => {
    switch (status) {
      case 'online':
      case 'active':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'offline':
      case 'inactive':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return '';
    }
  };

  // Group results by type
  const groupedResults = results.reduce((acc, result) => {
    const type = result.type;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(result);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  const groupOrder: Array<SearchResult['type']> = ['person', 'zone', 'device', 'log', 'customer', 'unit'];
  const sortedGroups = groupOrder.filter(type => groupedResults[type]?.length > 0);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Search Modal */}
      <div
        className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] px-4"
        onClick={onClose}
      >
        <div
          className="w-full max-w-2xl bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-in fade-in slide-in-from-top-4 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Search Input */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
            <Search className="h-5 w-5 text-gray-400 flex-shrink-0" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar pessoas, zonas, logs de acesso, clientes..."
              className="flex-1 outline-none text-sm placeholder-gray-400"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="p-1 hover:bg-gray-100 rounded-md transition-colors"
              >
                <X className="h-4 w-4 text-gray-400" />
              </button>
            )}
            <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-300 rounded">
              ESC
            </kbd>
          </div>

          {/* Results Container */}
          <div className="max-h-[60vh] overflow-y-auto">
            {isSearching ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
              </div>
            ) : searchTerm && results.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Search className="h-12 w-12 text-gray-300 mb-3" />
                <p className="text-sm font-medium text-gray-900">Nenhum resultado encontrado</p>
                <p className="text-sm text-gray-500 mt-1">Tente buscar por outro termo</p>
              </div>
            ) : searchTerm && results.length > 0 ? (
              <div className="py-2">
                {sortedGroups.map((groupType, groupIndex) => (
                  <div key={groupType} className={groupIndex > 0 ? 'mt-4' : ''}>
                    {/* Group Header */}
                    <div className="px-4 py-1.5 bg-gray-50 border-y border-gray-100">
                      <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">
                        {getResultTypeLabel(groupType).toUpperCase()} ({groupedResults[groupType].length})
                      </h3>
                    </div>

                    {/* Group Results */}
                    {groupedResults[groupType].map((result, localIndex) => {
                      const globalIndex = results.indexOf(result);
                      const Icon = getResultIcon(result.type);
                      const isSelected = globalIndex === selectedIndex;

                      return (
                        <button
                          key={result.id}
                          onClick={() => handleResultClick(result)}
                          className={cn(
                            'w-full flex items-start gap-3 px-4 py-2.5 transition-colors text-left',
                            isSelected ? 'bg-gray-100' : 'hover:bg-gray-50'
                          )}
                        >
                          {/* Show photo for person results, icon for others */}
                          {result.type === 'person' && (result.identity_photo_url || result.profile_picture_url) ? (
                            <img
                              src={getAbsoluteImageUrl(result.identity_photo_url || result.profile_picture_url)}
                              alt={result.title}
                              className="flex-shrink-0 w-9 h-9 rounded-lg object-cover"
                            />
                          ) : (
                            <div className={cn(
                              'flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center',
                              isSelected ? 'bg-black text-white' : 'bg-gray-100 text-gray-600'
                            )}>
                              <Icon className="h-4 w-4" />
                            </div>
                          )}

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <p className="text-sm font-semibold text-gray-900 truncate">
                                {result.title}
                              </p>
                              {result.status && (
                                <span className={cn(
                                  'inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border',
                                  getStatusColor(result.status)
                                )}>
                                  {result.status === 'active' || result.status === 'online' ? 'Ativo' : 'Inativo'}
                                </span>
                              )}
                            </div>

                            {result.subtitle && !(result.type === 'person' && result.subtitle.toLowerCase() === 'pessoa') && (
                              <p className="text-xs text-gray-600 mb-0.5">{result.subtitle}</p>
                            )}

                            {result.description && result.description.replace(/CPF:\s*/i, '').trim() && (
                              <p className="text-xs text-gray-500">{result.description}</p>
                            )}

                            {result.lastAccess && (
                              <div className="flex items-center gap-1 mt-1.5">
                                <Clock className="h-3 w-3 text-gray-400" />
                                <span className="text-xs text-gray-500">{result.lastAccess}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex-shrink-0 flex items-center">
                            <ArrowRight className={cn(
                              'h-4 w-4 transition-all',
                              isSelected ? 'text-black translate-x-0' : 'text-gray-300 -translate-x-1'
                            )} />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            ) : (
              // Recent searches and suggestions
              <div className="py-4">
                {recentSearches.length > 0 && (
                  <div className="px-4 mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <History className="h-4 w-4 text-gray-400" />
                        <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                          Buscas Recentes
                        </h3>
                      </div>
                      <button
                        onClick={clearRecentSearches}
                        className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        Limpar
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {recentSearches.map((term, index) => (
                        <button
                          key={index}
                          onClick={() => handleRecentSearchClick(term)}
                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-xs text-gray-700 transition-colors"
                        >
                          <Clock className="h-3 w-3 text-gray-400" />
                          {term}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="px-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp className="h-4 w-4 text-gray-400" />
                    <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                      Sugestões
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {[
                      { label: 'Ver todos os moradores', href: '/people' },
                      { label: 'Logs de acesso em tempo real', href: '/access-logs' },
                      { label: 'Gerenciar zonas de acesso', href: '/zones' },
                      { label: 'Lista de visitantes', href: '/visitors' }
                    ].map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => {
                          navigate(suggestion.href);
                          onClose();
                        }}
                        className="w-full flex items-center justify-between px-3 py-2 bg-gray-50 hover:bg-gray-100 rounded-lg text-sm text-gray-700 transition-colors text-left group"
                      >
                        <span>{suggestion.label}</span>
                        <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600 group-hover:translate-x-1 transition-all" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center gap-4 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs">↑</kbd>
                <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs">↓</kbd>
                <span>Navegar</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs">Enter</kbd>
                <span>Selecionar</span>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {results.length > 0 && `${results.length} resultado${results.length !== 1 ? 's' : ''}`}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default GlobalSearch;